import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FaArrowRight, 
  FaCheck, 
  FaStar, 
  FaTrophy,
  FaFire,
  FaPlay,
  FaUser,
  FaGraduationCap
} from 'react-icons/fa';

const LXDOnboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [userName, setUserName] = useState('');
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState('');
  const [showLion, setShowLion] = useState(false);
  const [lionMessage, setLionMessage] = useState('');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [currentQuizQuestion, setCurrentQuizQuestion] = useState(0);

  const interests = [
    { id: 'web', name: 'Web Development', icon: '💻', color: 'from-blue-500 to-cyan-500' },
    { id: 'mobile', name: 'Mobile Apps', icon: '📱', color: 'from-purple-500 to-pink-500' },
    { id: 'data', name: 'Data Science', icon: '📊', color: 'from-green-500 to-emerald-500' },
    { id: 'ai', name: 'AI & Machine Learning', icon: '🤖', color: 'from-orange-500 to-red-500' },
    { id: 'design', name: 'UI/UX Design', icon: '🎨', color: 'from-pink-500 to-rose-500' },
    { id: 'marketing', name: 'Digital Marketing', icon: '📈', color: 'from-indigo-500 to-purple-500' }
  ];

  const levels = [
    { id: 'beginner', name: 'Beginner', description: 'I\'m just starting out', icon: '🌱' },
    { id: 'intermediate', name: 'Intermediate', description: 'I have some experience', icon: '🌿' },
    { id: 'advanced', name: 'Advanced', description: 'I\'m quite experienced', icon: '🌳' }
  ];

  const quizQuestions = [
    {
      question: "What does HTML stand for?",
      options: [
        "Hyper Text Markup Language",
        "High Tech Modern Language",
        "Hyper Transfer Markup Language",
        "Home Tool Markup Language"
      ],
      correct: 0,
      explanation: "HTML is the standard markup language for creating web pages."
    },
    {
      question: "Which programming language is known for its simplicity?",
      options: ["Python", "C++", "Assembly", "Java"],
      correct: 0,
      explanation: "Python is famous for its clean, readable syntax."
    },
    {
      question: "What is the purpose of CSS?",
      options: [
        "To style web pages",
        "To create databases",
        "To write server-side code",
        "To manage networks"
      ],
      correct: 0,
      explanation: "CSS controls the visual presentation of HTML elements."
    }
  ];

  const lionWelcomeMessages = [
    "Welcome to LXD Ethiopia! I'm Leo, your learning companion! 🦁",
    "Let's get you started on your learning journey! 🚀",
    "I can't wait to see what you'll achieve! ✨"
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  useEffect(() => {
    if (step === 0) {
      setShowLion(true);
      setLionMessage(lionWelcomeMessages[0]);
    }
  }, [step]);

  const handleInterestToggle = (interestId) => {
    setSelectedInterests(prev => 
      prev.includes(interestId) 
        ? prev.filter(id => id !== interestId)
        : [...prev, interestId]
    );
  };

  const handleQuizAnswer = (answerIndex) => {
    const question = quizQuestions[currentQuizQuestion];
    const isCorrect = answerIndex === question.correct;
    
    setQuizAnswers(prev => ({
      ...prev,
      [currentQuizQuestion]: { answer: answerIndex, correct: isCorrect }
    }));

    if (currentQuizQuestion < quizQuestions.length - 1) {
      setCurrentQuizQuestion(prev => prev + 1);
      setLionMessage(isCorrect ? "Great job! Let's continue! 🦁" : "No worries! Keep going! 💪");
    } else {
      setLionMessage("Amazing! You're ready to start learning! 🎉");
      setTimeout(() => setStep(5), 2000);
    }
  };

  const handleNext = () => {
    if (step < 5) {
      setStep(prev => prev + 1);
      if (step === 0) {
        setLionMessage("What's your name, future learner? 👋");
      } else if (step === 1) {
        setLionMessage(`Nice to meet you, ${userName}! What interests you? 🤔`);
      } else if (step === 2) {
        setLionMessage("Great choices! What's your current level? 📚");
      } else if (step === 3) {
        setLionMessage("Perfect! Let's test your knowledge with a quick quiz! 🧠");
      }
    }
  };

  const handleComplete = () => {
    // Save user data and navigate to courses
    localStorage.setItem('lxdUserData', JSON.stringify({
      userName,
      interests: selectedInterests,
      level: selectedLevel,
      quizAnswers
    }));
    navigate('/courses');
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center"
          >
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 10, -10, 0]
              }}
              transition={{
                duration: 0.8,
                repeat: Infinity,
                repeatDelay: 1
              }}
              className="text-9xl mb-8"
            >
              🦁
            </motion.div>
            <h1 className="text-5xl font-bold text-gray-800 mb-4">
              Welcome to LXD Ethiopia!
            </h1>
            <p className="text-2xl text-gray-600 mb-8">
              Your journey to digital excellence starts here
            </p>
            <motion.button
              variants={itemVariants}
              onClick={handleNext}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-12 py-4 rounded-full font-semibold text-xl hover:opacity-90 transition-all flex items-center gap-3 mx-auto"
            >
              Get Started <FaArrowRight />
            </motion.button>
          </motion.div>
        );

      case 1:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-md mx-auto"
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
              What's your name?
            </h2>
            <motion.input
              variants={itemVariants}
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Enter your name"
              className="w-full px-6 py-4 text-xl border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none mb-6"
            />
            <motion.button
              variants={itemVariants}
              onClick={handleNext}
              disabled={!userName}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              Continue <FaArrowRight />
            </motion.button>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-2xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
              What interests you?
            </h2>
            <p className="text-gray-600 mb-8 text-center">Select all that apply</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              {interests.map((interest) => (
                <motion.button
                  key={interest.id}
                  variants={itemVariants}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleInterestToggle(interest.id)}
                  className={`p-6 rounded-2xl border-2 transition-all ${
                    selectedInterests.includes(interest.id)
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className={`text-4xl mb-3 bg-gradient-to-r ${interest.color} w-16 h-16 rounded-full flex items-center justify-center mx-auto`}>
                    {interest.icon}
                  </div>
                  <div className="font-semibold text-gray-800">{interest.name}</div>
                  {selectedInterests.includes(interest.id) && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-2 right-2 text-green-500"
                    >
                      <FaCheck />
                    </motion.div>
                  )}
                </motion.button>
              ))}
            </div>
            <motion.button
              variants={itemVariants}
              onClick={handleNext}
              disabled={selectedInterests.length === 0}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              Continue <FaArrowRight />
            </motion.button>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
              What's your experience level?
            </h2>
            <div className="space-y-4 mb-8">
              {levels.map((level) => (
                <motion.button
                  key={level.id}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedLevel(level.id)}
                  className={`w-full p-6 rounded-2xl border-2 transition-all flex items-center gap-4 ${
                    selectedLevel === level.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="text-4xl">{level.icon}</div>
                  <div className="text-left flex-1">
                    <div className="font-semibold text-gray-800 text-lg">{level.name}</div>
                    <div className="text-gray-600">{level.description}</div>
                  </div>
                  {selectedLevel === level.id && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="text-green-500 text-2xl"
                    >
                      <FaCheck />
                    </motion.div>
                  )}
                </motion.button>
              ))}
            </div>
            <motion.button
              variants={itemVariants}
              onClick={handleNext}
              disabled={!selectedLevel}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              Continue <FaArrowRight />
            </motion.button>
          </motion.div>
        );

      case 4:
        const question = quizQuestions[currentQuizQuestion];
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-2xl mx-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <span className="text-sm text-gray-500">
                Question {currentQuizQuestion + 1} of {quizQuestions.length}
              </span>
              <div className="flex gap-1">
                {quizQuestions.map((_, i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full ${
                      i < currentQuizQuestion ? 'bg-green-500' :
                      i === currentQuizQuestion ? 'bg-purple-500' :
                      'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">
              {question.question}
            </h2>

            <div className="space-y-4 mb-8">
              {question.options.map((option, index) => (
                <motion.button
                  key={index}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleQuizAnswer(index)}
                  className="w-full p-5 rounded-xl border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all text-left"
                >
                  <span className="font-semibold text-gray-800">{option}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        );

      case 5:
        const correctAnswers = Object.values(quizAnswers).filter(a => a.correct).length;
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center max-w-xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.8 }}
              className="text-8xl mb-6"
            >
              🎉
            </motion.div>
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              You're All Set!
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Welcome to LXD Ethiopia, {userName}!
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-purple-50 rounded-xl p-4">
                <div className="text-3xl font-bold text-purple-600">{selectedInterests.length}</div>
                <div className="text-sm text-gray-600">Interests Selected</div>
              </div>
              <div className="bg-green-50 rounded-xl p-4">
                <div className="text-3xl font-bold text-green-600">{correctAnswers}/{quizQuestions.length}</div>
                <div className="text-sm text-gray-600">Quiz Score</div>
              </div>
            </div>

            <motion.button
              variants={itemVariants}
              onClick={handleComplete}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-12 py-4 rounded-full font-semibold text-xl hover:opacity-90 transition-all flex items-center gap-3 mx-auto"
            >
              Start Learning <FaPlay />
            </motion.button>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50 pt-20">
      <div className="container mx-auto px-6 py-12">
        {/* Lion Mascot */}
        <AnimatePresence>
          {showLion && (
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              className="mb-8 text-center"
            >
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  repeatDelay: 1
                }}
                className="relative inline-block"
              >
                <div className="text-7xl">🦁</div>
                {/* Speech bubble */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-16 left-1/2 -translate-x-1/2 bg-white rounded-2xl px-6 py-3 shadow-lg whitespace-nowrap"
                >
                  <p className="text-gray-800 font-semibold">{lionMessage}</p>
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white transform rotate-45" />
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Progress Bar */}
        {step > 0 && step < 5 && (
          <div className="max-w-2xl mx-auto mb-8">
            <div className="flex items-center gap-2 mb-2">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 h-2 rounded-full ${
                    i < step ? 'bg-gradient-to-r from-purple-600 to-blue-600' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <div className="text-center text-sm text-gray-500">
              Step {step} of 5
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          {renderStep()}
        </div>

        {/* Skip Button */}
        {step > 0 && step < 5 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center mt-8"
          >
            <button
              onClick={handleComplete}
              className="text-gray-500 hover:text-gray-700 text-sm underline"
            >
              Skip and go to courses
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default LXDOnboarding;
